import { useEffect, useRef, useState } from 'react'
import { FileChip } from '@/components/ui/file-chip'
import { cn } from '~/@/lib/utils'
import { DocumentType } from '~/common/generated/apollo-operations'
import { FileCellDocument } from '~/components/records/record-cell-details/types'
import { FileInlineImage } from '~/components/table-cells/FileTableCells/FileInlineImage'

/**
 * Derives DocumentType from file extension
 */
function getDocumentTypeFromExtension(fileName: string): DocumentType {
  const extension = fileName.split('.').pop()?.toLowerCase()

  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return DocumentType.JPG
    case 'png':
      return DocumentType.PNG
    case 'svg':
      return DocumentType.SVG
    case 'gif':
      return DocumentType.GIF
    case 'webp':
      return DocumentType.WEBP
    case 'pdf':
      return DocumentType.PDF
    case 'doc':
      return DocumentType.DOC
    case 'docx':
      return DocumentType.DOCX
    case 'xls':
      return DocumentType.XLS
    case 'xlsx':
      return DocumentType.XLSX
    case 'csv':
      return DocumentType.CSV
    case 'txt':
      return DocumentType.TXT
    default:
      // Default to TXT for unknown file extensions
      return DocumentType.TXT
  }
}

interface FileChipsDisplayProps {
  files: Record<string, File>
  className?: string
  disabled?: boolean
  displayImagesInline?: boolean
  getFileUrl?: (file: File) => string
  onRemoveFile: (id: string) => void
}

type FileDocumentItem = {
  id: string
  file: File
  document: FileCellDocument
}

/**
 * Displays file chips or inline images for uploaded files.
 */
export function FileChipsDisplay({
  files,
  className,
  disabled,
  displayImagesInline,
  getFileUrl,
  onRemoveFile,
}: FileChipsDisplayProps) {
  const [fileDocuments, setFileDocuments] = useState<FileDocumentItem[]>([])
  const blobUrlsRef = useRef<Map<string, string>>(new Map())

  // Incremental blob URL management - handle file additions/deletions
  useEffect(() => {
    const currentFileIds = new Set(Object.keys(files))
    const previousFileIds = new Set(blobUrlsRef.current.keys())

    // Find removed files and revoke their blob URLs immediately
    previousFileIds.forEach((id) => {
      if (!currentFileIds.has(id)) {
        const blobUrl = blobUrlsRef.current.get(id)
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl)
          blobUrlsRef.current.delete(id)
        }
      }
    })

    // Create documents array, reusing existing blob URLs or creating new ones
    const currentFileDocs = Object.entries(files).map(([id, file]) => {
      let blobUrl = blobUrlsRef.current.get(id)

      // Only create blob URL if we don't have one for this file ID
      if (!blobUrl) {
        blobUrl = URL.createObjectURL(file)
        blobUrlsRef.current.set(id, blobUrl)
      }

      return {
        id,
        file,
        document: {
          __typename: 'Document' as const,
          id,
          name: file.name,
          documentType: getDocumentTypeFromExtension(file.name),
          storageUrl: blobUrl,
          signature: null,
        },
      }
    })

    setFileDocuments(currentFileDocs)
  }, [files])

  // Cleanup any remaining references on component unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl)
      })
      blobUrlsRef.current.clear()
    }
  }, [])

  if (Object.keys(files).length === 0) {
    return null
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {fileDocuments.map(({ id, file, document }) =>
        displayImagesInline ? (
          <FileInlineImage
            key={`${id}-${file.name}`}
            disabled={disabled}
            document={document}
            onDelete={() => onRemoveFile(id)}
          />
        ) : (
          <FileChip
            key={`${id}-${file.name}`}
            disabled={disabled}
            fileName={file.name}
            fileUrl={getFileUrl ? getFileUrl(file) : undefined}
            onDelete={() => onRemoveFile(id)}
          />
        ),
      )}
    </div>
  )
}
